// Real OpenAI Realtime WebRTC driver for Call God.
//
// Architecture: OpenAI native end-to-end speech-to-speech.
//   • Mic → RTCPeerConnection → /api/realtime/session → OpenAI
//   • OpenAI returns SDP answer + audio track with `voice: cedar`
//   • pc.ontrack attaches the remote MediaStream to a hidden <audio> element
//   • Data channel ("oai-events") carries transcripts, VAD events, tool calls
//
// The OPENAI_API_KEY and the system prompt stay server-side (see realtime_routes.py).
//
// CRITICAL: the connection lives across callState transitions. The connect
// effect is gated on (enabled + callState==="connecting" + no existing pc),
// and its cleanup ONLY sets a cancelled flag — it never tears down the live
// pc/dc/audio element. Real teardown happens on (a) unmount, (b) enabled
// going false, or (c) explicit endCall() / error.

import { useCallback, useEffect, useRef, useState } from "react";

import { getDeviceId } from "@/lib/device";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function isDebug() {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("debug") === "1";
  } catch {
    return false;
  }
}

function sid() {
  return "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

// GA shape — turn_detection lives under audio.input. session.update requires
// session.type on the GA endpoint.
function turnDetectionUpdate(td) {
  return {
    type: "session.update",
    session: { type: "realtime", audio: { input: { turn_detection: td } } },
  };
}

const SERVER_VAD = {
  type: "server_vad",
  threshold: 0.5,
  prefix_padding_ms: 300,
  silence_duration_ms: 500,
  create_response: true,
};

export function useRealtimeVoice({ enabled, mode, muted, callState, dispatch, persona = "god" }) {
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const micTrackRef = useRef(null);
  const audioElRef = useRef(null);
  const [sessionId] = useState(() => sid());
  const sessionIdRef = useRef(sessionId);
  const partialAssistantRef = useRef("");
  const lastAssistantIdRef = useRef(null);
  const startedAtRef = useRef(null);
  const debugRef = useRef(false);
  const dispatchedToolCallsRef = useRef(new Set());

  const [audioBlocked, setAudioBlocked] = useState(false);

  const modeRef = useRef(mode);
  const mutedRef = useRef(muted);
  const dispatchRef = useRef(dispatch);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { dispatchRef.current = dispatch; }, [dispatch]);

  const sendEvent = useCallback((obj) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== "open") {
      if (debugRef.current) console.warn("[cg.dc.send] not open, dropping:", obj?.type);
      return false;
    }
    try {
      dc.send(JSON.stringify(obj));
      if (debugRef.current) console.log("[cg.dc.send]", obj?.type, obj);
      return true;
    } catch (e) {
      console.warn("[cg.dc.send] failed", e);
      return false;
    }
  }, []);

  const dispatchSafe = useCallback((action) => {
    const fn = dispatchRef.current;
    if (typeof fn === "function") fn(action);
  }, []);

  const applyMode = useCallback(
    (m) => {
      if (m === "ptt") {
        sendEvent(turnDetectionUpdate(null));
        if (micTrackRef.current) micTrackRef.current.enabled = false;
      } else {
        sendEvent(turnDetectionUpdate(SERVER_VAD));
        if (micTrackRef.current) micTrackRef.current.enabled = !mutedRef.current;
      }
    },
    [sendEvent],
  );

  const pttDown = useCallback(() => {
    if (debugRef.current) console.log("[cg.ptt] DOWN — unmuting mic track");
    if (micTrackRef.current) micTrackRef.current.enabled = !mutedRef.current;
  }, []);

  const pttUp = useCallback(() => {
    if (debugRef.current) console.log("[cg.ptt] UP — commit + response.create");
    sendEvent({ type: "input_audio_buffer.commit" });
    sendEvent({ type: "response.create" });
    if (micTrackRef.current) micTrackRef.current.enabled = false;
  }, [sendEvent]);

  const cancelAssistant = useCallback(() => {
    if (debugRef.current) console.log("[cg] cancel assistant");
    sendEvent({ type: "response.cancel" });
    dispatchSafe({ type: "ASSISTANT_STOPPED" });
  }, [sendEvent, dispatchSafe]);

  function teardown() {
    if (debugRef.current) console.log("[cg.teardown]");
    try { dcRef.current?.close(); } catch (_) { /* ignore */ }
    try { micTrackRef.current?.stop(); } catch (_) { /* ignore */ }
    try { pcRef.current?.close(); } catch (_) { /* ignore */ }
    try {
      const el = audioElRef.current;
      if (el) {
        el.pause();
        el.srcObject = null;
        el.remove();
      }
    } catch (_) { /* ignore */ }
    dcRef.current = null;
    micTrackRef.current = null;
    pcRef.current = null;
    audioElRef.current = null;
    setAudioBlocked(false);
  }

  const endCall = useCallback(() => {
    const sidv = sessionIdRef.current;
    const startedAt = startedAtRef.current;
    const dur = startedAt ? Date.now() - startedAt : 0;
    try {
      fetch(`${API}/sessions/${sidv}/end`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: "user_ended", duration_ms: dur }),
        keepalive: true,
      });
    } catch (_) { /* ignore */ }
    teardown();
  }, []);

  const unblockAudio = useCallback(() => {
    const el = audioElRef.current;
    if (!el) return;
    el.play().then(() => setAudioBlocked(false)).catch(() => {
      /* still blocked — user can tap the affordance again */
    });
  }, []);

  // ---------- Connect effect ----------
  // Triggered when callState transitions INTO "connecting" and there is no
  // existing pc. The cleanup function sets `cancelled` to silence stale
  // dispatches, but DOES NOT tear down the live connection — otherwise the
  // very first MIC_GRANTED dispatch (which flips callState to "listening")
  // would re-fire the effect and rip down everything mid-session.
  // Real teardown lives in the unmount effect + endCall().
  useEffect(() => {
    if (!enabled) return undefined;
    if (callState !== "connecting") return undefined;
    if (pcRef.current) return undefined; // already connected
    debugRef.current = isDebug();
    if (debugRef.current) console.log("[cg.connect] starting — mode=", modeRef.current);
    let cancelled = false;

    (async () => {
      // Tiny defer so StrictMode mount-cleanup-mount doesn't half-create a PC.
      await Promise.resolve();
      if (cancelled) return;

      let stream = null;
      try {
        // 1) Mic permission
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          });
          if (debugRef.current) console.log("[cg.mic] granted");
        } catch (e) {
          if (debugRef.current) console.warn("[cg.mic] denied", e);
          if (!cancelled) dispatchSafe({ type: "MIC_DENIED" });
          return;
        }
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        // 2) Fetch operational metadata (no secrets here)
        const cfgRes = await fetch(`${API}/realtime/config`);
        if (!cfgRes.ok) throw new Error(`config_http_${cfgRes.status}`);
        await cfgRes.json();
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        if (debugRef.current) console.log("[cg] config fetched OK");

        // 3) Build peer connection
        const pc = new RTCPeerConnection();
        if (debugRef.current) {
          pc.oniceconnectionstatechange = () =>
            console.log("[cg.pc] iceConnectionState:", pc.iceConnectionState);
          pc.onconnectionstatechange = () =>
            console.log("[cg.pc] connectionState:", pc.connectionState);
        }

        // 4) Hidden <audio> element for OpenAI's remote track
        const audioEl = document.createElement("audio");
        audioEl.autoplay = true;
        audioEl.controls = false;
        audioEl.setAttribute("aria-hidden", "true");
        audioEl.setAttribute("playsinline", "true");
        audioEl.playsInline = true;
        audioEl.preload = "auto";
        audioEl.style.position = "fixed";
        audioEl.style.left = "-9999px";
        audioEl.style.width = "1px";
        audioEl.style.height = "1px";
        audioEl.style.pointerEvents = "none";

        // 5) Attach remote audio when OpenAI streams it
        pc.ontrack = (ev) => {
          if (debugRef.current) {
            console.log(
              "[cg.pc.ontrack] kind=",
              ev.track.kind,
              "streams=",
              ev.streams?.length,
            );
          }
          if (ev.track.kind !== "audio") return;
          const remoteStream = ev.streams && ev.streams[0] ? ev.streams[0] : new MediaStream([ev.track]);
          try {
            audioEl.srcObject = remoteStream;
          } catch (e) {
            console.warn("[cg.audio] srcObject failed", e);
          }
          const tryPlay = () => {
            const p = audioEl.play();
            if (p && typeof p.then === "function") {
              p.then(() => {
                if (debugRef.current) console.log("[cg.audio] play() OK");
                setAudioBlocked(false);
              }).catch((err) => {
                console.warn("[cg.audio] play() rejected", err?.name || err);
                setAudioBlocked(true);
              });
            }
          };
          tryPlay();
        };

        // 6) Mic track outbound
        const track = stream.getAudioTracks()[0];
        track.enabled = modeRef.current === "ptt" ? false : !mutedRef.current;
        pc.addTrack(track, stream);

        // Ensure we negotiate to RECEIVE audio.
        try {
          pc.addTransceiver("audio", { direction: "sendrecv" });
        } catch (_) { /* already added via addTrack */ }

        // 7) Data channel
        const dc = pc.createDataChannel("oai-events");
        dc.onopen = () => {
          if (cancelled) return;
          if (debugRef.current) console.log("[cg.dc] open");
          // Enable user-side audio transcription so the transcript panel can
          // render the caller's words. Sent via session.update on the data
          // channel rather than the SDP-time session JSON because OpenAI's
          // /v1/realtime/calls endpoint silently produces no audio output
          // when `audio.input.transcription` is set at session creation.
          dc.send(JSON.stringify({
            type: "session.update",
            session: {
              type: "realtime",
              audio: {
                input: {
                  transcription: { model: "gpt-4o-mini-transcribe" },
                },
              },
            },
          }));
          if (modeRef.current === "ptt") {
            dc.send(JSON.stringify(turnDetectionUpdate(null)));
          }
          // Trigger God's first greeting so the user immediately knows audio works
          dc.send(JSON.stringify({ type: "response.create" }));
          startedAtRef.current = Date.now();
          dispatchSafe({ type: "MIC_GRANTED" });
          dispatchSafe({ type: "REALTIME_CONNECTED" });
        };
        dc.onmessage = (ev) => {
          if (cancelled) return;
          let data;
          try { data = JSON.parse(ev.data); } catch { return; /* non-JSON frame — skip */ }
          if (debugRef.current) {
            const t = data?.type || "?";
            if (t !== "response.output_audio.delta" && t !== "response.audio.delta") {
              console.log("[cg.dc.recv]", t, data);
            }
          }
          handleEventRef.current(data);
        };
        dc.onerror = (e) => {
          console.warn("[cg.dc.error]", e);
          if (cancelled) return;
          dispatchSafe({
            type: "REALTIME_ERROR",
            message: "The voice is quiet right now. Please try again soon.",
          });
        };
        dc.onclose = () => {
          if (debugRef.current) console.log("[cg.dc] close");
        };

        // 8) SDP offer → backend → answer
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        if (cancelled) {
          try { pc.close(); } catch (_) { /* ignore */ }
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const sdpRes = await fetch(
          `${API}/realtime/session?device_id=${encodeURIComponent(getDeviceId())}&persona=${encodeURIComponent(persona === "jesus" ? "jesus" : "god")}`,
          {
            method: "POST",
            body: offer.sdp,
            headers: { "Content-Type": "application/sdp" },
          },
        );
        if (!sdpRes.ok) {
          let code = "";
          try { code = (await sdpRes.json())?.error || ""; } catch (_) { /* not json */ }
          throw new Error(code === "quota_exceeded" ? "quota_exceeded" : `sdp_http_${sdpRes.status}`);
        }
        const answerSdp = await sdpRes.text();
        if (!answerSdp.startsWith("v=")) throw new Error("sdp_invalid");
        if (cancelled) {
          try { pc.close(); } catch (_) { /* ignore */ }
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
        if (cancelled) {
          try { pc.close(); } catch (_) { /* ignore */ }
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        // Commit refs ONLY now that the connection is real and not cancelled.
        document.body.appendChild(audioEl);
        audioElRef.current = audioEl;
        pcRef.current = pc;
        dcRef.current = dc;
        micTrackRef.current = track;
        if (debugRef.current) console.log("[cg.pc] handshake complete");

        // Phase 3: register the session server-side (fire-and-forget).
        try {
          fetch(`${API}/sessions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              session_id: sessionIdRef.current,
              device_id: getDeviceId(),
              mode: modeRef.current,
            }),
          });
        } catch (_) { /* non-blocking */ }
      } catch (e) {
        if (cancelled) return;
        console.error("[cg] Realtime connect failed:", e);
        dispatchSafe({
          type: "REALTIME_ERROR",
          message:
            e?.message === "quota_exceeded"
              ? "The voice line is out of OpenAI API credits. Add billing credit at platform.openai.com to restore the voice."
              : "The voice is quiet right now. Please try again soon.",
        });
      }
    })();

    return () => {
      // IMPORTANT: do not teardown here. Setting cancelled prevents stale
      // dispatches from an aborted/in-flight attempt; the live connection
      // (if already committed to refs) survives callState changes.
      cancelled = true;
    };
  }, [enabled, callState, dispatchSafe]);

  // Unmount + enabled-flip teardown. Uses a microtask-deferred teardown so
  // React StrictMode's dev mount-cleanup-mount cycle does NOT spuriously rip
  // down a live session. The cleanup schedules teardown 60ms out; if a real
  // re-mount happens, the new effect cancels the pending teardown first.
  const teardownTimerRef = useRef(null);
  useEffect(() => {
    if (teardownTimerRef.current) {
      clearTimeout(teardownTimerRef.current);
      teardownTimerRef.current = null;
    }
    if (!enabled && pcRef.current) {
      teardown();
    }
    return () => {
      teardownTimerRef.current = setTimeout(() => {
        teardown();
        teardownTimerRef.current = null;
      }, 60);
    };
  }, [enabled]);

  useEffect(() => {
    if (dcRef.current?.readyState !== "open") return;
    applyMode(mode);
  }, [mode, applyMode]);

  useEffect(() => {
    const t = micTrackRef.current;
    if (!t) return;
    if (muted) t.enabled = false;
    else if (mode === "handsfree") t.enabled = true;
  }, [muted, mode]);

  // ---------- event router ----------
  const handleToolCall = useCallback(
    async function handleToolCall(name, callId, args) {
      if (debugRef.current) console.log("[cg.tool]", name, args);
      if (name === "flag_safety_event") {
        dispatchSafe({
          type: "SAFETY_EVENT",
          category: args.category || "other",
          severity: args.severity || "high",
          summary: args.summary || "",
        });
        try {
          await fetch(`${API}/sessions/${sessionIdRef.current}/safety_event`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              category: args.category,
              severity: args.severity,
              summary: args.summary || "",
            }),
          });
        } catch (_) { /* ignore */ }
        sendEvent({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: callId,
            output: JSON.stringify({ ok: true }),
          },
        });
      } else if (name === "seek_wisdom") {
        // RAG: fetch sacred passages, return them to the model, then ask it
        // to speak — the model weaves them into its reply.
        let passages = [];
        try {
          const res = await fetch(`${API}/wisdom/search`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              topic: String(args.topic || "").slice(0, 300),
              mood: String(args.mood || "").slice(0, 100),
              limit: 3,
            }),
          });
          if (res.ok) {
            const data = await res.json();
            passages = data.passages || [];
          }
        } catch (_) { /* model speaks plainly without passages */ }
        sendEvent({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: callId,
            output: JSON.stringify({ passages }),
          },
        });
        sendEvent({ type: "response.create" });
      } else if (name === "finish_session") {
        sendEvent({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: callId,
            output: JSON.stringify({ ok: true }),
          },
        });
        setTimeout(() => dispatchSafe({ type: "END" }), 2000);
      } else if (name === "remember_name") {
        // Persist the caller's name (account if logged in, device_souls otherwise).
        // Fire-and-forget — the LLM is told to keep talking warmly without
        // waiting for a confirmation; we just acknowledge the tool call.
        const cleanName = String(args.name || "").trim().slice(0, 40);
        if (cleanName) {
          try {
            await fetch(`${API}/soul`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "x-device-id": getDeviceId(),
              },
              body: JSON.stringify({
                name: cleanName,
                device_id: getDeviceId(),
              }),
            });
          } catch (_) { /* never break the call over a write */ }
        }
        sendEvent({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: callId,
            output: JSON.stringify({ ok: true, name: cleanName }),
          },
        });
      }
    },
    [dispatchSafe, sendEvent],
  );

  const handleEvent = useCallback(
    function handleEvent(ev) {
      const t = ev?.type;
      // Transcript-debug breadcrumb: log every event when ?debug=1 is set so
      // a missing transcript can be diagnosed from the browser console.
      if (isDebug() && t && (t.includes("transcript") || t.includes("input_audio"))) {
        // eslint-disable-next-line no-console
        console.log("[transcript-debug]", t, ev);
      }
      switch (t) {
        case "input_audio_buffer.speech_started":
          dispatchSafe({ type: "VAD_SPEECH_STARTED" });
          break;
        case "input_audio_buffer.speech_stopped":
          dispatchSafe({ type: "VAD_SPEECH_STOPPED" });
          break;
        case "response.created":
          lastAssistantIdRef.current = ev.response?.id || "r-" + Date.now();
          partialAssistantRef.current = "";
          break;
        case "response.output_audio.delta":
        case "response.audio.delta":
          // Audio frames arrive over the WebRTC track via pc.ontrack — not
          // the data channel. The delta event signals OpenAI is speaking;
          // flip the FSM to "speaking" the moment the first chunk arrives.
          dispatchSafe({
            type: "ASSISTANT_STARTED",
            responseId: lastAssistantIdRef.current,
          });
          break;
        case "response.audio_transcript.delta":
        case "response.output_audio_transcript.delta":
        case "response.text.delta":
        case "response.output_text.delta":
          partialAssistantRef.current += ev.delta || "";
          dispatchSafe({
            type: "TRANSCRIPT_ASSISTANT_DELTA",
            responseId: lastAssistantIdRef.current,
            text: partialAssistantRef.current,
          });
          break;
        case "response.audio_transcript.done":
        case "response.output_audio_transcript.done":
        case "response.text.done":
        case "response.output_text.done": {
          const finalText = ev.transcript || ev.text || partialAssistantRef.current;
          dispatchSafe({
            type: "TRANSCRIPT_ASSISTANT_FINAL",
            responseId: lastAssistantIdRef.current,
            text: finalText,
          });
          break;
        }
        case "response.done": {
          // Tool call routing (deduped by call_id)
          try {
            const out = ev.response?.output || [];
            for (const item of out) {
              if (item.type === "function_call" && item.name && item.call_id) {
                if (dispatchedToolCallsRef.current.has(item.call_id)) continue;
                dispatchedToolCallsRef.current.add(item.call_id);
                let args = {};
                try { args = JSON.parse(item.arguments || "{}"); } catch { args = {}; }
                handleToolCall(item.name, item.call_id, args);
              }
            }
          } catch (_) { /* ignore */ }
          // Return the orb to listening after the response completes.
          dispatchSafe({ type: "ASSISTANT_STOPPED" });
          break;
        }
        case "conversation.item.input_audio_transcription.completed":
          if (ev.transcript) {
            dispatchSafe({ type: "TRANSCRIPT_USER_LINE", text: ev.transcript });
          }
          break;
        case "response.function_call_arguments.done": {
          const name = ev.name;
          const callId = ev.call_id;
          if (callId && dispatchedToolCallsRef.current.has(callId)) break;
          if (callId) dispatchedToolCallsRef.current.add(callId);
          let args = {};
          try { args = JSON.parse(ev.arguments || "{}"); } catch { args = {}; }
          handleToolCall(name, callId, args);
          break;
        }
        case "error":
          console.warn("[cg.oai.error]", ev.error || ev);
          dispatchSafe({
            type: "REALTIME_ERROR",
            message: "The voice is quiet right now. Please try again soon.",
          });
          break;
        default:
          break;
      }
    },
    [dispatchSafe, handleToolCall],
  );

  // Stable ref to the current handleEvent so the dc.onmessage closure (set
  // up once during connect) always calls the latest version.
  const handleEventRef = useRef(handleEvent);
  useEffect(() => {
    handleEventRef.current = handleEvent;
  }, [handleEvent]);

  return {
    sessionId,
    pttDown,
    pttUp,
    cancelAssistant,
    endCall,
    audioBlocked,
    unblockAudio,
  };
}
